import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore, UploadFile, Visibility } from "@mui/icons-material";
import {
  useBulkImportApply,
  useBulkImportPreview,
  type BulkImportPreviewRow,
} from "@/hooks/useCRM";

const EXAMPLE_CSV = `first_name,last_name,email,company_name,job_title,phone
Alice,Anderson,alice@acme.co.uk,Acme Ltd,Director,07123 456789
Bob,Brown,bob@acme.co.uk,Acme Ltd,Engineer,
Carol,Chen,carol@beta.com,Beta Strategy LLP,Partner,`;

function statusChip(status: "new" | "existing" | "blank") {
  if (status === "new") return <Chip label="new" color="success" size="small" />;
  if (status === "existing") return <Chip label="existing" color="default" size="small" />;
  return <Chip label="—" size="small" variant="outlined" />;
}

export default function CRMImport() {
  const [raw, setRaw] = useState("");
  const [sourceLabel, setSourceLabel] = useState("bulk_import");
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const previewMutation = useBulkImportPreview();
  const applyMutation = useBulkImportApply();
  const preview = previewMutation.data;
  const applyResult = applyMutation.data;

  const handleFile = async (file: File) => {
    const text = await file.text();
    setRaw(text);
    previewMutation.reset();
    applyMutation.reset();
  };

  const onPreview = async () => {
    if (!raw.trim()) {
      setSnackbar({ open: true, message: "Paste some rows first", severity: "error" });
      return;
    }
    try {
      await previewMutation.mutateAsync(raw);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Preview failed";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  const onApply = async () => {
    try {
      const result = await applyMutation.mutateAsync({ raw, source_label: sourceLabel });
      setSnackbar({
        open: true,
        message: `Created ${result.companies_created} companies + ${result.individuals_created} contacts`,
        severity: "success",
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Apply failed";
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Bulk Import — Companies + Contacts
        </Typography>
        <Button component={RouterLink} to="/crm">
          Back to CRM
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Paste rows from a spreadsheet (CSV or tab-separated) or upload a .csv file. The first row
        must be the header. We dedupe contacts by email and companies by normalised name (so "Acme
        Ltd" and "Acme" match). Existing rows are reused — never overwritten.
      </Typography>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Expected column headers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Required:</strong> <code>first_name</code>, <code>last_name</code>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Optional:</strong> <code>email</code>, <code>company_name</code>,{" "}
            <code>job_title</code>, <code>phone</code>, <code>linkedin_url</code>,{" "}
            <code>notes</code>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Extra columns are ignored. Email is used to dedupe individuals — rows without an email
            are always treated as new.
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: "#fafafa",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              whiteSpace: "pre",
            }}
          >
            {EXAMPLE_CSV}
          </Box>
          <Button size="small" sx={{ mt: 1 }} onClick={() => setRaw(EXAMPLE_CSV)}>
            Load example into the box below
          </Button>
        </AccordionDetails>
      </Accordion>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Paste rows here"
              value={raw}
              onChange={(e) => {
                setRaw(e.target.value);
                previewMutation.reset();
                applyMutation.reset();
              }}
              multiline
              minRows={8}
              fullWidth
              placeholder="first_name,last_name,email,company_name,…"
              InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.85rem" } }}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                disabled={previewMutation.isPending}
              >
                Or upload .csv
                <input
                  type="file"
                  accept=".csv,text/csv,text/tab-separated-values,text/plain"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                  }}
                />
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                startIcon={<Visibility />}
                onClick={onPreview}
                disabled={previewMutation.isPending || !raw.trim()}
              >
                {previewMutation.isPending ? "Analysing…" : "Preview"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {preview && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
              <SummaryTile label="Rows parsed" value={preview.parsed_count} />
              <SummaryTile
                label="New companies"
                value={preview.new_companies.length}
                color="success"
              />
              <SummaryTile
                label="Existing companies matched"
                value={preview.existing_companies_matched}
              />
              <SummaryTile label="New contacts" value={preview.new_individuals} color="success" />
              <SummaryTile
                label="Existing contacts matched"
                value={preview.existing_individuals_matched}
              />
              {preview.errors.length > 0 && (
                <SummaryTile label="Errors" value={preview.errors.length} color="error" />
              )}
            </Stack>

            {preview.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {preview.errors.length} row(s) will be skipped:
                </Typography>
                {preview.errors.slice(0, 10).map((e, i) => (
                  <Typography key={i} variant="body2">
                    Line {e.line_number}: {e.message}
                  </Typography>
                ))}
              </Alert>
            )}

            {preview.new_companies.length > 0 && (
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    New companies that will be created ({preview.new_companies.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" component="div">
                    {preview.new_companies.map((n) => (
                      <Chip
                        key={n}
                        label={n}
                        size="small"
                        sx={{ m: 0.25 }}
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">
                  All rows ({preview.rows.length}) — review then apply
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <PreviewTable rows={preview.rows} />
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField
                label="Source label"
                value={sourceLabel}
                onChange={(e) => setSourceLabel(e.target.value)}
                size="small"
                helperText="Stored on each new row's `source` field"
                sx={{ width: 240 }}
              />
              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                color="primary"
                onClick={onApply}
                disabled={applyMutation.isPending || preview.parsed_count === 0 || !!applyResult}
              >
                {applyMutation.isPending
                  ? "Importing…"
                  : applyResult
                    ? "Imported"
                    : `Import ${preview.new_companies.length + preview.new_individuals} new rows`}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {applyResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Import complete
          </Typography>
          <Typography variant="body2">
            Created {applyResult.companies_created} companies (reused {applyResult.companies_reused}
            ), created {applyResult.individuals_created} contacts (reused{" "}
            {applyResult.individuals_reused}). <RouterLink to="/crm">View in CRM →</RouterLink>
          </Typography>
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function SummaryTile({
  label,
  value,
  color = "default",
}: {
  label: string;
  value: number;
  color?: "default" | "success" | "error" | "warning";
}) {
  const bg =
    color === "success"
      ? "#e8f5e9"
      : color === "error"
        ? "#ffebee"
        : color === "warning"
          ? "#fff8e1"
          : "#f5f5f5";
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: bg,
        minWidth: 150,
        borderRadius: 1,
      }}
    >
      <Typography variant="h6">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function PreviewTable({ rows }: { rows: BulkImportPreviewRow[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No rows parsed.
      </Typography>
    );
  }
  return (
    <TableContainer sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Company status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.line_number}>
              <TableCell>{r.line_number}</TableCell>
              <TableCell>
                {r.first_name} {r.last_name}
                {r.job_title && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {r.job_title}
                  </Typography>
                )}
              </TableCell>
              <TableCell>{r.email ?? "—"}</TableCell>
              <TableCell>{r.company_name ?? "—"}</TableCell>
              <TableCell>{statusChip(r.individual_status)}</TableCell>
              <TableCell>{statusChip(r.company_status)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
